/**
 * Browser telemetry — Azure Application Insights (RUM).
 *
 * Completes the end-to-end trace. The backends were already correlated with each
 * other (Broker → IndexingTool → market-report) via W3C `traceparent` propagated
 * by httpx; this module makes the *browser* the origin of that trace, so a slow
 * chat reply can be followed from the user's click all the way to the pgvector
 * query that caused it.
 *
 * What it collects: page views, SPA route changes, AJAX timing, JS exceptions,
 * unhandled promise rejections.
 *
 * Cross-origin correlation requires both halves:
 *   • here — `enableCorsCorrelation` plus `correlationHeaderDomains` listing the
 *     API hosts, or the SDK will not attach `traceparent` to cross-origin calls;
 *   • server — the API must accept the header. Both FastAPI apps allow it via
 *     `allow_headers=["*"]` and echo it back through `expose_headers`.
 *
 * Disabled entirely when REACT_APP_APPINSIGHTS_CONNECTION_STRING is unset, which
 * is the normal state for local development.
 */

import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { BACKEND_URL, IS_DEV } from '../environment';

const CONNECTION_STRING = process.env.REACT_APP_APPINSIGHTS_CONNECTION_STRING || '';

let appInsights = null;

/** Hostnames the SDK may attach correlation headers to. */
const correlationHeaderDomains = () => {
  const domains = [
    'chatapi.aibrokerhub.com',
    'indexingapi.aibrokerhub.com',
  ];
  try {
    const backendHost = new URL(BACKEND_URL).hostname;
    if (backendHost && !domains.includes(backendHost)) domains.push(backendHost);
  } catch {
    /* BACKEND_URL malformed — fall back to the static list */
  }
  if (IS_DEV) domains.push('localhost');
  return domains;
};

export function initTelemetry() {
  if (appInsights) return appInsights;
  if (!CONNECTION_STRING) {
    if (IS_DEV) {
      // eslint-disable-next-line no-console
      console.info('[telemetry] REACT_APP_APPINSIGHTS_CONNECTION_STRING unset — RUM disabled');
    }
    return null;
  }

  try {
    appInsights = new ApplicationInsights({
      config: {
        connectionString: CONNECTION_STRING,
        // W3C traceparent is what the Python services understand. The default
        // mode also emits the legacy Request-Id header; AI_AND_W3C keeps both so
        // correlation survives if any hop is on an older SDK.
        distributedTracingMode: 2, // DistributedTracingModes.AI_AND_W3C
        enableCorsCorrelation: true,
        correlationHeaderDomains: correlationHeaderDomains(),
        enableAutoRouteTracking: true, // react-router navigations become page views
        enableRequestHeaderTracking: false, // headers can carry the bearer token
        enableResponseHeaderTracking: false,
        enableUnhandledPromiseRejectionTracking: true,
        autoTrackPageVisitTime: true,
        disableFetchTracking: false,
        disableAjaxTracking: false,
        loggingLevelConsole: IS_DEV ? 1 : 0,
      },
    });
    appInsights.loadAppInsights();

    // Strip anything sensitive before a telemetry item leaves the browser, and
    // stamp the service name so RUM rows sit alongside the backend services in
    // the same dashboards.
    appInsights.addTelemetryInitializer((item) => {
      item.tags = item.tags || {};
      item.tags['ai.cloud.role'] = 'frontend';
      // Query strings can carry reset tokens and OAuth codes.
      if (item.baseData && typeof item.baseData.uri === 'string') {
        item.baseData.uri = item.baseData.uri.split('?')[0];
      }
      if (item.baseData && typeof item.baseData.url === 'string') {
        item.baseData.url = item.baseData.url.split('?')[0];
      }
      return true;
    });

    appInsights.trackPageView();
    return appInsights;
  } catch (err) {
    // Telemetry must never break the app.
    // eslint-disable-next-line no-console
    console.warn('[telemetry] init failed', err);
    return null;
  }
}

/** Associate subsequent RUM with a signed-in user. Call after login. */
export function setTelemetryUser(userId) {
  if (!appInsights || !userId) return;
  try {
    appInsights.setAuthenticatedUserContext(String(userId), undefined, true);
  } catch {
    /* non-fatal */
  }
}

/** Clear the association on logout so the next session is not attributed. */
export function clearTelemetryUser() {
  if (!appInsights) return;
  try {
    appInsights.clearAuthenticatedUserContext();
  } catch {
    /* non-fatal */
  }
}

/** Record a product event (e.g. chat_message_sent, item_saved). */
export function trackEvent(name, properties = {}) {
  if (!appInsights) return;
  try {
    appInsights.trackEvent({ name }, properties);
  } catch {
    /* non-fatal */
  }
}

/** Record a handled exception with context. */
export function trackError(error, properties = {}) {
  if (!appInsights) return;
  try {
    appInsights.trackException({ exception: error }, properties);
  } catch {
    /* non-fatal */
  }
}

const telemetry = {
  initTelemetry, setTelemetryUser, clearTelemetryUser, trackEvent, trackError,
};

export default telemetry;
